/**
 * Shop Background Prefetcher Service
 * 
 * Asynchronously downloads the shop JSON and cosmectics images in the background
 * while the user is reading/browsing the index (landing) page.
 * 
 * Features:
 * - Uses requestIdleCallback (with setTimeout fallback) to avoid interfering with UI thread.
 * - Concurrency control (max 3-4 parallel image downloads) to prevent network saturation.
 * - Image prioritisation: featured/top sections downloaded first, then remaining items.
 * - Dual caching: populates browser HTTP/memory cache and Cache API ('gf-shop-v1').
 * - Deduplication: tracks prefetched URLs to avoid redundant requests.
 */

import { RawShopEntry, parseRawEntryItem } from '../utils/fortniteShopParser';
import { useShopStore } from '../stores/useShopStore';
import { useCurrencyStore } from '../stores/useCurrencyStore';

const PREFETCHED_URLS = new Set<string>();
let isPrefetchStarted = false;

/**
 * Extracts all unique cosmetic image URLs from shop entries,
 * ordered by appearance (top shop sections first).
 */
export function extractShopImageUrls(entries: RawShopEntry[]): string[] {
  const urls: string[] = [];
  const seen = new Set<string>();

  const addUrl = (url?: string | null) => {
    if (!url || typeof url !== 'string') return;
    const clean = url.trim();
    if (
      clean &&
      (clean.startsWith('http://') ||
        clean.startsWith('https://') ||
        clean.startsWith('/uploads/') ||
        clean.startsWith('/assets/')) &&
      !seen.has(clean)
    ) {
      seen.add(clean);
      urls.push(clean);
    }
  };

  for (const entry of entries) {
    // 1. Parsed item primary image
    try {
      const parsed = parseRawEntryItem(entry);
      if (parsed?.image) addUrl(parsed.image);
    } catch {}

    // 2. New display asset material instances & render images
    if (entry.newDisplayAsset?.materialInstances) {
      for (const mat of entry.newDisplayAsset.materialInstances) {
        if (mat.images) {
          for (const imgUrl of Object.values(mat.images)) {
            addUrl(imgUrl);
          }
        }
      }
    }
    if (entry.newDisplayAsset?.renderImages) {
      for (const rImg of entry.newDisplayAsset.renderImages) {
        if (rImg.image) addUrl(rImg.image);
      }
    }

    // 3. Sub-items / brItems images
    if (Array.isArray(entry.brItems)) {
      for (const item of entry.brItems) {
        if (item.images?.featured) addUrl(item.images.featured);
        if (item.images?.icon) addUrl(item.images.icon);
        if (item.images?.large) addUrl(item.images.large);
        if (item.images?.smallIcon) addUrl(item.images.smallIcon);
      }
    }

    // 4. Bundle image
    if (entry.bundle?.image) {
      addUrl(entry.bundle.image);
    }
  }

  return urls;
}

/**
 * Preloads a single image into the browser's HTTP/memory cache.
 */
function preloadImage(url: string): Promise<void> {
  if (PREFETCHED_URLS.has(url)) return Promise.resolve();
  PREFETCHED_URLS.add(url);

  return new Promise((resolve) => {
    const img = new Image();
    img.decoding = 'async';
    img.loading = 'eager';

    const timer = setTimeout(() => {
      done();
    }, 8000);

    const done = () => {
      clearTimeout(timer);
      img.onload = null;
      img.onerror = null;
      resolve();
    };

    img.onload = done;
    img.onerror = done;
    img.src = url;
  });
}

/**
 * Download a list of image URLs with concurrency limit during idle periods.
 */
async function processImageQueue(
  urls: string[],
  concurrency = 3,
  onProgress?: (loaded: number, total: number) => void
): Promise<void> {
  const pending = [...urls];
  const total = urls.length;
  let loaded = 0;

  const runWorker = async () => {
    while (pending.length > 0) {
      const nextUrl = pending.shift();
      if (!nextUrl) break;
      await preloadImage(nextUrl);
      loaded++;
      if (onProgress) onProgress(loaded, total);
      // Small tick between downloads to yield thread to user interactions
      await new Promise((res) => setTimeout(res, 20));
    }
  };

  const workers = Array.from({ length: Math.min(concurrency, pending.length) }, () => runWorker());
  await Promise.all(workers);
}

/**
 * Sync shop JSON to Cache API and localStorage for instant offline/fast load.
 */
async function syncToCacheApi(data: any): Promise<void> {
  try {
    const entries = data?.data?.entries || data?.entries || [];
    if (Array.isArray(entries) && entries.length > 0) {
      try {
        localStorage.setItem(
          'gf_shop_raw_cache_v2',
          JSON.stringify({ timestamp: Date.now(), entries })
        );
      } catch {}
    }
    if (typeof window !== 'undefined' && 'caches' in window) {
      const cache = await caches.open('gf-shop-v1');
      const jsonStr = JSON.stringify(data);
      await cache.put(
        '/__gf_shop_cache__',
        new Response(jsonStr, {
          headers: {
            'Content-Type': 'application/json',
            'Cache-Control': 'public, max-age=3600',
          },
        })
      );
    }
  } catch {}
}

/**
 * Initiates shop background prefetching.
 * Should be called when the index/landing page loads.
 */
export function startShopBackgroundPrefetch(): void {
  if (typeof window === 'undefined' || isPrefetchStarted) return;
  isPrefetchStarted = true;

  const scheduleTask = (task: () => void) => {
    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(task, { timeout: 2500 });
    } else {
      setTimeout(task, 400);
    }
  };

  scheduleTask(async () => {
    try {
      const shopState = useShopStore.getState();
      const currency = useCurrencyStore.getState().currency;

      // 1. Ensure shop JSON is fetched
      let entries = shopState.rawEntries;
      if (!entries || entries.length === 0) {
        if (shopState.isLoading) {
          // A fetch is already running in App.tsx: wait for it to complete without aborting
          await new Promise<void>((resolve) => {
            const unsubscribe = useShopStore.subscribe((state) => {
              if (!state.isLoading && state.rawEntries && state.rawEntries.length > 0) {
                unsubscribe();
                resolve();
              }
            });
            setTimeout(() => {
              unsubscribe();
              resolve();
            }, 10000);
          });
          entries = useShopStore.getState().rawEntries;
        } else {
          await shopState.fetchShop(currency);
          entries = useShopStore.getState().rawEntries;
        }
      }

      if (entries && entries.length > 0) {
        // Cache JSON in Cache API & localStorage
        syncToCacheApi({ status: 200, data: { entries } });

        // 2. Extract and queue all image URLs
        const imageUrls = extractShopImageUrls(entries);
        if (imageUrls.length > 0) {
          // Download top 15 images (most visible) with higher priority
          const topImages = imageUrls.slice(0, 15);
          const restImages = imageUrls.slice(15);

          await processImageQueue(topImages, 4);

          // Rest of images downloaded during subsequent idle periods
          scheduleTask(() => {
            processImageQueue(restImages, 3);
          });
        }
      }
    } catch (err) {
      console.warn('[ShopPrefetcher] Background prefetch error:', err);
    }
  });
}
